import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import Swal from 'sweetalert2';
import { AuthService } from 'src/app/services/auth/auth.service';
import {
    AuditEntry, CopyPlanItem, EventCatalog, EventProductsService,
} from 'src/app/services/event-products/event-products.service';
import { ImportZone, normalizeText } from 'src/app/shared/product-import/product-import.model';
import { verifyEventAdminCredentials } from 'src/app/data/event-admin-guard';

/** Una fila de la matriz: un nombre de producto y lo que hay de él en cada zona. */
export interface AuditRow {
    id: string;
    name: string;
    cells: { [zoneKey: string]: AuditEntry[] };
    missing: number;
    duplicated: number;
    hidden: number;
}

export interface ZoneStats {
    total: number;
    missing: number;
    duplicated: number;
    hidden: number;
}

type Step = 'view' | 'working' | 'done';

/**
 * Comparación de productos entre las zonas de un evento: qué falta en cada
 * barra, qué está repetido dentro de una barra y qué está oculto. Desde aquí
 * se copian los faltantes a todas las zonas y se eliminan los repetidos.
 * Todo sale de una sola lectura del evento (EventProductsService.getEventCatalog).
 */
@Component({
    selector: 'app-zone-audit',
    templateUrl: './zone-audit.component.html',
    styleUrls: ['./zone-audit.component.scss'],
})
export class ZoneAuditComponent implements OnInit {

    @Input() eventKey: string = '';
    @Input() eventName: string = '';
    /** Roles que pueden copiar y eliminar (los mismos que vacían una zona). */
    @Input() canManage: boolean = false;
    @Input() currentZoneKey: string = '';

    step: Step = 'view';
    loading = true;
    error = '';

    catalog: EventCatalog = { zones: [], entries: [] };
    rows: AuditRow[] = [];
    stats: { [zoneKey: string]: ZoneStats } = {};
    distinct = 0;
    totalMissing = 0;
    totalDuplicated = 0;

    onlyDiff = true;
    copyInventory = false;

    progressText = '';
    doneText = '';

    constructor(
        private modalCtrl: ModalController,
        private service: EventProductsService,
        public auth: AuthService,
    ) { }

    ngOnInit() {
        this.reload();
    }

    close() {
        this.modalCtrl.dismiss({ changed: this.step === 'done' || this.doneText !== '' });
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  LECTURA Y MATRIZ
    // ═══════════════════════════════════════════════════════════════════════

    async reload() {
        this.loading = true;
        this.error = '';
        try {
            this.catalog = await this.service.getEventCatalog(this.eventKey);
            this.build();
        } catch (e) {
            console.error('No se pudo leer el evento:', e);
            this.error = 'No se pudieron leer los productos del evento. Intenta de nuevo.';
        }
        this.loading = false;
    }

    private build() {
        const byName: { [id: string]: AuditRow } = {};
        const order: string[] = [];
        this.catalog.entries.forEach(entry => {
            const id = normalizeText(entry.name) || entry.key;
            if (!byName[id]) {
                byName[id] = { id, name: entry.name, cells: {}, missing: 0, duplicated: 0, hidden: 0 };
                order.push(id);
            }
            (byName[id].cells[entry.zoneKey] = byName[id].cells[entry.zoneKey] || []).push(entry);
        });

        this.stats = {};
        this.catalog.zones.forEach(z => this.stats[z.key] = { total: 0, missing: 0, duplicated: 0, hidden: 0 });
        this.totalMissing = 0;
        this.totalDuplicated = 0;

        this.rows = order.map(id => {
            const row = byName[id];
            this.catalog.zones.forEach(zone => {
                const list = row.cells[zone.key] || [];
                const st = this.stats[zone.key];
                st.total += list.length;
                if (list.length === 0) { row.missing++; st.missing++; }
                if (list.length > 1) { row.duplicated++; st.duplicated += list.length - 1; }
                if (list.length > 0 && list.every(e => e.hidden)) { row.hidden++; st.hidden++; }
            });
            this.totalMissing += row.missing;
            this.totalDuplicated += row.duplicated;
            return row;
        }).sort((a, b) => a.name.localeCompare(b.name));
        this.distinct = this.rows.length;
    }

    get visibleRows(): AuditRow[] {
        return this.onlyDiff ? this.rows.filter(r => r.missing > 0 || r.duplicated > 0 || r.hidden > 0) : this.rows;
    }

    get zones(): ImportZone[] {
        return this.catalog.zones;
    }

    /**
     * Estado de una celda para pintarla: ok · missing · dup · hidden · ref.
     * "ref" = solo hay una referencia: cuenta como presente en el monitor,
     * pero la caja no la ofrece (solo vende productos propios de la zona).
     */
    cellState(row: AuditRow, zone: ImportZone): string {
        const list = row.cells[zone.key] || [];
        if (list.length === 0) return 'missing';
        if (list.length > 1) return 'dup';
        if (list.every(e => e.hidden)) return 'hidden';
        if (list.every(e => e.isReference)) return 'ref';
        return 'ok';
    }

    cellCount(row: AuditRow, zone: ImportZone): number {
        return (row.cells[zone.key] || []).length;
    }

    cellTitle(row: AuditRow, zone: ImportZone): string {
        const list = row.cells[zone.key] || [];
        if (list.length === 0) return `Falta en ${zone.name}`;
        const refs = list.filter(e => e.isReference).length;
        const hidden = list.filter(e => e.hidden).length;
        const parts = [`${list.length} en ${zone.name}`];
        if (refs) parts.push(`${refs} ${refs === 1 ? 'referencia (la caja no la ofrece)' : 'referencias (la caja no las ofrece)'}`);
        if (hidden) parts.push(`${hidden} ${hidden === 1 ? 'oculto' : 'ocultos'}`);
        return parts.join(' · ');
    }

    isCurrent(zone: ImportZone): boolean {
        return !!this.currentZoneKey && zone.key === this.currentZoneKey;
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  COPIAR FALTANTES
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Para cada zona sin el producto, de dónde copiarlo: mejor un producto
     * propio y visible; si no, uno propio oculto; si solo hay referencias, se
     * copia el producto original como producto propio (la caja no vende
     * referencias).
     */
    private missingPlan(): { plan: CopyPlanItem[]; sources: { [zoneKey: string]: ImportZone } } {
        const plan: CopyPlanItem[] = [];
        const sources: { [zoneKey: string]: ImportZone } = {};
        const rank = (e: AuditEntry) => (e.isReference ? 2 : 0) + (e.hidden ? 1 : 0);
        this.rows.forEach(row => {
            if (row.missing === 0) return;
            let source: ImportZone = null;
            let entry: AuditEntry = null;
            this.zones.forEach(z => (row.cells[z.key] || []).forEach(e => {
                if (!entry || rank(e) < rank(entry)) { entry = e; source = z; }
            }));
            if (!source) return;
            const product: any = { $key: entry.brick, name: entry.name };
            this.zones.forEach(zone => {
                if ((row.cells[zone.key] || []).length > 0) return;
                sources[source.key] = source;
                plan.push({ product: { ...product, __source: source.key }, zone, action: 'create', existingKey: '', note: '' });
            });
        });
        return { plan, sources };
    }

    async copyMissing() {
        if (!this.canManage || this.step !== 'view') return;
        const { plan, sources } = this.missingPlan();
        if (plan.length === 0) {
            Swal.fire('Nada que copiar', 'Todas las zonas ya tienen los mismos productos.', 'info');
            return;
        }
        const zonesTouched = plan.map(i => i.zone.key).filter((k, i, all) => all.indexOf(k) === i).length;
        const confirm: any = await Swal.fire({
            title: `¿Copiar ${plan.length} ${plan.length === 1 ? 'producto' : 'productos'} a ${zonesTouched} ${zonesTouched === 1 ? 'zona' : 'zonas'}?`,
            text: this.copyInventory
                ? 'Cada copia nace con el inventario de la zona de origen.'
                : 'Las copias nacen con inventario 0 y ocultas: se muestran en la caja cuando les cargues existencias.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, copiar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#912721',
            cancelButtonColor: '#92949c',
        });
        if (!confirm || !confirm.value) return;

        this.step = 'working';
        this.error = '';
        const positions: { [zoneKey: string]: number } = {};
        this.zones.forEach(z => positions[z.key] = this.stats[z.key] ? this.stats[z.key].total : 0);
        let created = 0;
        try {
            const sourceKeys = Object.keys(sources);
            for (let i = 0; i < sourceKeys.length; i++) {
                const source = sources[sourceKeys[i]];
                const group = plan.filter(item => item.product.__source === source.key);
                this.progressText = `Copiando desde ${source.name} (${i + 1} de ${sourceKeys.length})…`;
                const result = await this.service.copyProducts(group, { sourceZone: source, eventKey: this.eventKey, copyInventory: this.copyInventory }, positions);
                created += result.created;
                group.forEach(item => positions[item.zone.key] = (positions[item.zone.key] || 0) + 1);
            }
            this.doneText = `${created} ${created === 1 ? 'producto copiado' : 'productos copiados'}.`;
        } catch (e) {
            console.error('Falló la copia de faltantes:', e);
            this.error = 'No se pudieron copiar todos los productos. Revisa la conexión; lo que sí se copió ya aparece abajo.';
        }
        this.step = 'view';
        await this.reload();
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  ELIMINAR REPETIDOS
    // ═══════════════════════════════════════════════════════════════════════

    /** En cada zona, de cada nombre repetido se conserva uno (visible y primero en el orden) y sobran los demás. */
    private duplicatesToDelete(): AuditEntry[] {
        const extra: AuditEntry[] = [];
        this.rows.forEach(row => {
            this.zones.forEach(zone => {
                const list = (row.cells[zone.key] || []).slice();
                if (list.length < 2) return;
                list.sort((a, b) => Number(a.hidden) - Number(b.hidden) || a.position - b.position || a.key.localeCompare(b.key));
                extra.push(...list.slice(1));
            });
        });
        return extra;
    }

    async deleteDuplicates() {
        if (!this.canManage || this.step !== 'view') return;
        const extra = this.duplicatesToDelete();
        if (extra.length === 0) {
            Swal.fire('Sin repetidos', 'Ninguna zona tiene productos repetidos.', 'info');
            return;
        }
        if (!(await this.askAdmin(`Eliminar ${extra.length} ${extra.length === 1 ? 'producto repetido' : 'productos repetidos'} es permanente.`))) return;
        const confirm: any = await Swal.fire({
            title: `¿Eliminar ${extra.length} ${extra.length === 1 ? 'repetido' : 'repetidos'}?`,
            text: 'De cada producto repetido en una zona se conserva uno (el visible y primero en el orden) y se eliminan los demás. Las órdenes ya registradas no cambian.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#c0392b',
            cancelButtonColor: '#92949c',
        });
        if (!confirm || !confirm.value) return;

        this.step = 'working';
        this.progressText = 'Eliminando repetidos…';
        this.error = '';
        try {
            const count = await this.service.deleteEntries(extra, 'Repetidos en la zona eliminados desde la comparación de zonas');
            this.doneText = `${count} ${count === 1 ? 'documento eliminado' : 'documentos eliminados'}.`;
        } catch (e) {
            console.error('No se pudieron eliminar los repetidos:', e);
            this.error = 'No se pudieron eliminar todos los repetidos. Revisa la conexión e intenta de nuevo.';
        }
        this.step = 'view';
        await this.reload();
    }

    /** Misma credencial que vaciar una zona o eliminar las órdenes del evento. */
    private async askAdmin(text: string): Promise<boolean> {
        const auth: any = await Swal.fire({
            title: 'Autorización requerida',
            html: `
                <p style="margin:0 0 14px;font-size:.92rem;color:#6b7280;line-height:1.45;">${text} Confirma con el nombre y la contraseña de autorización.</p>
                <input id="za-auth-name" class="swal2-input" placeholder="Nombre autorizado" autocomplete="off" style="margin:6px 0;width:100%;box-sizing:border-box;">
                <input id="za-auth-pass" type="password" class="swal2-input" placeholder="Contraseña" autocomplete="new-password" style="margin:6px 0;width:100%;box-sizing:border-box;">
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Verificar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#912721',
            cancelButtonColor: '#92949c',
            preConfirm: () => {
                const name = (document.getElementById('za-auth-name') as HTMLInputElement).value;
                const pass = (document.getElementById('za-auth-pass') as HTMLInputElement).value;
                if (!verifyEventAdminCredentials(name, pass)) {
                    Swal.showValidationMessage('El nombre o la contraseña no coinciden.');
                    return false;
                }
                return true;
            },
        });
        return !!(auth && auth.value);
    }
}
